import {useEffect, useState} from 'react'

import {CHAR_SPEED, LOADING_LINES, POST_REDACT_WAIT, POST_TYPE_WAIT, REDACT_DURATION} from '../constants'
import {LoadingLine} from './LoadingLine'

export const Calculating = ({onComplete}: {onComplete: () => void}) => {
  const [completedLines, setCompletedLines] = useState(0)
  const [fadedIn, setFadedIn] = useState(false)

  // Fade in on mount
  useEffect(() => {
    const t = requestAnimationFrame(() => setFadedIn(true))
    return () => cancelAnimationFrame(t)
  }, [])

  // After all lines complete, switch to results
  useEffect(() => {
    if (completedLines < LOADING_LINES.length) {
      return
    }

    const t = setTimeout(onComplete, 800)
    return () => clearTimeout(t)
  }, [completedLines, onComplete])

  // Calculate start delay for each line based on previous lines
  const getLineStartDelay = (lineIndex: number): number => {
    let delay = 0

    delay += 300 // wait for fade-in

    for (let i = 0; i < lineIndex; i++) {
      const line = LOADING_LINES[i]
      delay += line.text.length * CHAR_SPEED + POST_TYPE_WAIT + REDACT_DURATION + POST_REDACT_WAIT
    }

    return delay
  }

  return (
    <div
      className="relative min-h-screen bg-[#171717] transition-opacity duration-300 ease-in"
      style={{opacity: fadedIn ? 1 : 0}}
    >
      {/* Loading screen content */}
      <div className="absolute inset-0">
        {/* Base overlay */}
        <div className="absolute inset-0 bg-[rgba(19,18,18,0.85)] backdrop-blur-[6px]" />

        {/* Grain texture */}
        <div className="absolute inset-0" style={{opacity: 0.15}}>
          <svg className="size-full">
            <filter id="grain-static">
              <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" seed={42} stitchTiles="stitch" />
            </filter>
            <rect width="100%" height="100%" filter="url(#grain-static)" />
          </svg>
        </div>

        {/* Radial vignette */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%)',
          }}
        />

        {/* Text sequence */}
        <div
          className="absolute inset-0 flex flex-col items-start justify-center gap-3 px-6 md:px-0 md:pl-[calc(50%-130px)]"
          style={{transform: 'translateY(-100px)'}}
        >
          {LOADING_LINES.map((line, i) => (
            <LoadingLine
              key={line.keyword}
              text={line.text}
              keyword={line.keyword}
              startDelay={getLineStartDelay(i)}
              onDone={() => setCompletedLines((c) => Math.max(c, i + 1))}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
